import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Button,
  Stack,
  Chip
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { FilterAlt as FilterIcon, FilterAltOff as FilterOffIcon } from '@mui/icons-material';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#A4DE6C'];

function Dashboard({ data, orderBy, order, handleSort }) {
  const [filters, setFilters] = useState({
    partner: null,
    meetingType: null,
    interlockAccount: null
  });

  // Apply filters to data and deduplicate by Meeting ID (except for Leaderboard)
  const filteredData = useMemo(() => {
    // First, create a Map to deduplicate by Meeting ID
    const meetingMap = new Map();
    
    return data.filter(meeting => 
      (!filters.partner || meeting['Partner Name'] === filters.partner) &&
      (!filters.meetingType || meeting['Meeting Type'] === filters.meetingType) &&
      (!filters.interlockAccount || meeting['Interlock Account'] === filters.interlockAccount)
    );
  }, [data, filters]);

  // Get deduplicated data for components that need it
  const deduplicatedData = useMemo(() => {
    const meetingMap = new Map();
    
    filteredData.forEach(meeting => {
      const meetingId = meeting['Meeting ID'];
      if (!meetingMap.has(meetingId) || 
          new Date(meeting['Timestamp']) > new Date(meetingMap.get(meetingId)['Timestamp'])) {
        meetingMap.set(meetingId, meeting);
      }
    });
    
    return Array.from(meetingMap.values());
  }, [filteredData]);

  // Calculate dashboard metrics with deduplicated data
  const dashboardMetrics = useMemo(() => {
    // Partner metrics
    const partnerMeetings = new Map();
    const partnerTrends = new Map();
    const quarterlyActivity = new Map();
    const currentDate = new Date();
    const lastQuarterDate = new Date();
    lastQuarterDate.setMonth(currentDate.getMonth() - 3);

    // Monthly trend tracking
    const monthlyTrend = new Map();
    const monthlyInterlockTrend = new Map();
    const last6Months = new Array(6).fill(0).map((_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.toISOString().slice(0, 7); // YYYY-MM format
    }).reverse();
    
    last6Months.forEach(month => {
      monthlyTrend.set(month, 0);
      monthlyInterlockTrend.set(month, 0);
    });

    deduplicatedData.forEach(meeting => {
      const partner = meeting['Partner Name'];
      const meetingDate = new Date(meeting['Meeting Date'] || meeting['Timestamp']);
      const meetingType = meeting['Meeting Type'];
      const isInterlockMeeting = meetingType && (
        meetingType.toLowerCase().includes('interlock') ||
        meetingType.includes('PIO') ||
        meetingType.includes('PAO')
      );
      
      if (!isNaN(meetingDate.getTime())) { // Check if we have a valid date
        // Partner meeting counts
        if (partner && partner !== 'N/A') {
          partnerMeetings.set(partner, (partnerMeetings.get(partner) || 0) + 1);
          
          // Track quarterly engagement
          if (meetingDate >= lastQuarterDate) {
            quarterlyActivity.set(partner, (quarterlyActivity.get(partner) || 0) + 1);
          }
          
          // Track month-over-month trends
          const monthKey = meetingDate.toISOString().slice(0, 7); // YYYY-MM format
          if (monthlyTrend.has(monthKey)) {
            monthlyTrend.set(monthKey, monthlyTrend.get(monthKey) + 1);
            
            // Track Interlock meetings separately
            if (isInterlockMeeting && monthlyInterlockTrend.has(monthKey)) {
              monthlyInterlockTrend.set(monthKey, monthlyInterlockTrend.get(monthKey) + 1);
            }
          }
          
          // Track partner monthly trends
          if (!partnerTrends.has(partner)) {
            partnerTrends.set(partner, new Map());
          }
          const partnerMonthly = partnerTrends.get(partner);
          partnerMonthly.set(monthKey, (partnerMonthly.get(monthKey) || 0) + 1);
        }
      }
    });

    // Calculate partner engagement scores
    const partnerScores = new Map();
    partnerMeetings.forEach((meetings, partner) => {
      const quarterlyMeetings = quarterlyActivity.get(partner) || 0;
      const monthlyTrend = partnerTrends.get(partner);
      const lastThreeMonths = Array.from(monthlyTrend.values()).slice(-3);
      const trendScore = lastThreeMonths.reduce((acc, curr) => acc + curr, 0) / 3;
      
      // Score based on total meetings, quarterly activity, and recent trend
      const score = (meetings * 0.4) + (quarterlyMeetings * 0.4) + (trendScore * 0.2);
      partnerScores.set(partner, Math.round(score * 100) / 100);
    });

    const partnerData = Array.from(partnerMeetings.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Meeting type metrics
    const meetingTypes = new Map();
    deduplicatedData.forEach(meeting => {
      const type = meeting['Meeting Type'];
      if (type) {
        meetingTypes.set(type, (meetingTypes.get(type) || 0) + 1);
      }
    });

    const meetingTypeData = Array.from(meetingTypes.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Top performing partners (based on engagement score)
    const topPartners = Array.from(partnerScores.entries())
      .map(([name, score]) => ({
        name,
        score,
        meetings: partnerMeetings.get(name),
        quarterlyMeetings: quarterlyActivity.get(name) || 0
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    // Convert monthly trend data to array format
    const monthlyTrendData = Array.from(monthlyTrend.entries())
      .map(([month, count]) => ({
        month,
        count
      }));

    const monthlyInterlockTrendData = Array.from(monthlyInterlockTrend.entries())
      .map(([month, count]) => ({
        month,
        count
      }));

    return {
      partnerData,
      meetingTypeData,
      topPartners,
      monthlyTrendData,
      monthlyInterlockTrendData,
      totalMeetings: deduplicatedData.length,
      activePartners: partnerMeetings.size,
      quarterlyActivePartners: Array.from(quarterlyActivity.values()).filter(count => count > 0).length
    };
  }, [deduplicatedData]);

  const handleFilterClick = (type, value) => {
    setFilters(prev => {
      // If clicking the same filter, remove it
      if (prev[type] === value) {
        return { ...prev, [type]: null };
      }
      // Otherwise, set the new filter
      return { ...prev, [type]: value };
    });
  };

  const clearFilters = () => {
    setFilters({
      partner: null,
      meetingType: null,
      interlockAccount: null
    });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Partner Dashboard
      </Typography>

      {/* Active Filters */}
      {(filters.partner || filters.meetingType || filters.interlockAccount) && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">Active Filters:</Typography>
            {filters.partner && (
              <Chip
                label={`Partner: ${filters.partner}`}
                onDelete={() => handleFilterClick('partner', filters.partner)}
                color="primary"
              />
            )}
            {filters.meetingType && (
              <Chip
                label={`Type: ${filters.meetingType}`}
                onDelete={() => handleFilterClick('meetingType', filters.meetingType)}
                color="primary"
              />
            )}
            {filters.interlockAccount && (
              <Chip
                label={`Interlock Account: ${filters.interlockAccount}`}
                onDelete={() => handleFilterClick('interlockAccount', filters.interlockAccount)}
                color="primary"
              />
            )}
            <Button
              startIcon={<FilterOffIcon />}
              onClick={clearFilters}
              variant="outlined"
              size="small"
            >
              Clear All
            </Button>
          </Stack>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Executive Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Total Partner Engagement
                </Typography>
                <Typography variant="h3" color="primary">
                  {dashboardMetrics.totalMeetings}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Total Meetings
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Active Partners
                </Typography>
                <Typography variant="h3" color="primary">
                  {dashboardMetrics.activePartners}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Partners with Recent Activity
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Quarterly Active Partners
                </Typography>
                <Typography variant="h3" color="primary">
                  {dashboardMetrics.quarterlyActivePartners}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Partners Active This Quarter
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Monthly Trend Chart */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Meeting Trends
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={dashboardMetrics.monthlyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      return new Date(year, month - 1).toLocaleString('default', { month: 'short' });
                    }}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [`${value} meetings`, 'Total Meetings']}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-');
                      return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                    }}
                  />
                  <Bar dataKey="count" fill="#8884d8" name="Total Meetings" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Monthly Interlock Trends */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Interlock Trends
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={dashboardMetrics.monthlyInterlockTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      return new Date(year, month - 1).toLocaleString('default', { month: 'short' });
                    }}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [`${value} meetings`, 'Interlock Meetings']}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-');
                      return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                    }}
                  />
                  <Bar dataKey="count" fill="#82ca9d" name="Interlock Meetings" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Top Performing Partners */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Partners
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell align="right">Engagement Score</TableCell>
                    <TableCell align="right">Total Meetings</TableCell>
                    <TableCell align="right">Quarterly Meetings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardMetrics.topPartners.map((partner) => (
                    <TableRow key={partner.name}>
                      <TableCell component="th" scope="row">
                        {partner.name}
                      </TableCell>
                      <TableCell align="right">
                        <Typography color={partner.score >= 7 ? 'success.main' : 'warning.main'}>
                          {partner.score.toFixed(1)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{partner.meetings}</TableCell>
                      <TableCell align="right">{partner.quarterlyMeetings}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Partner Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom align="center">
              Top Partners by Meeting Count
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer>
                <BarChart data={dashboardMetrics.partnerData.slice(0, 7)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill="#8884d8"
                    onClick={(data) => handleFilterClick('partner', data.name)}
                    cursor="pointer"
                  >
                    {dashboardMetrics.partnerData.slice(0, 7).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.name === filters.partner ? '#ff7300' : COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Meeting Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Meeting Type Distribution
            </Typography>
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={dashboardMetrics.meetingTypeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      value,
                      index
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      const percent = ((value / deduplicatedData.length) * 100).toFixed(0);
                      if (percent < 5) return null; // Don't show labels for small segments

                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#666"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          style={{ fontSize: '12px' }}
                        >
                          {`${percent}%`}
                        </text>
                      );
                    }}
                  >
                    {dashboardMetrics.meetingTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${value} (${((value / deduplicatedData.length) * 100).toFixed(0)}%)`,
                      name
                    ]}
                  />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{
                      paddingLeft: '20px',
                      fontSize: '12px',
                      maxWidth: '180px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Meetings Table */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Meetings {filters.partner && `(${filters.partner})`} {filters.meetingType && `(${filters.meetingType})`} {filters.interlockAccount && `(${filters.interlockAccount})`}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Meeting Date'}
                        direction={orderBy === 'Meeting Date' ? order : 'asc'}
                        onClick={() => handleSort('Meeting Date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Partner Name'}
                        direction={orderBy === 'Partner Name' ? order : 'asc'}
                        onClick={() => handleSort('Partner Name')}
                      >
                        Partner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Title'}
                        direction={orderBy === 'Title' ? order : 'asc'}
                        onClick={() => handleSort('Title')}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Meeting Type'}
                        direction={orderBy === 'Meeting Type' ? order : 'asc'}
                        onClick={() => handleSort('Meeting Type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Interlock Account'}
                        direction={orderBy === 'Interlock Account' ? order : 'asc'}
                        onClick={() => handleSort('Interlock Account')}
                      >
                        Interlock Account
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deduplicatedData
                    .sort((a, b) => {
                      // Handle date sorting
                      if (orderBy === 'Meeting Date') {
                        return order === 'asc'
                          ? new Date(a[orderBy]) - new Date(b[orderBy])
                          : new Date(b[orderBy]) - new Date(a[orderBy]);
                      }
                      // Handle string sorting
                      const aValue = (a[orderBy] || '').toString().toLowerCase();
                      const bValue = (b[orderBy] || '').toString().toLowerCase();
                      if (order === 'asc') {
                        return aValue.localeCompare(bValue);
                      }
                      return bValue.localeCompare(aValue);
                    })
                    .slice(0, 10)
                    .map((meeting) => (
                      <TableRow 
                        key={meeting.id}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell>{new Date(meeting['Meeting Date']).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleFilterClick('partner', meeting['Partner Name'])}
                            sx={{ 
                              textTransform: 'none',
                              color: filters.partner === meeting['Partner Name'] ? 'primary.main' : 'inherit'
                            }}
                          >
                            {meeting['Partner Name']}
                          </Button>
                        </TableCell>
                        <TableCell>{meeting.Title}</TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleFilterClick('meetingType', meeting['Meeting Type'])}
                            sx={{ 
                              textTransform: 'none',
                              color: filters.meetingType === meeting['Meeting Type'] ? 'primary.main' : 'inherit'
                            }}
                          >
                            {meeting['Meeting Type']}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleFilterClick('interlockAccount', meeting['Interlock Account'])}
                            sx={{ 
                              textTransform: 'none',
                              color: filters.interlockAccount === meeting['Interlock Account'] ? 'primary.main' : 'inherit'
                            }}
                            disabled={!meeting['Interlock Account']}
                          >
                            {meeting['Interlock Account'] || '-'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
